import { AuthResponse, GoogleCredentialResponse } from '@/types/auth';
import { apiService } from './api';

class AuthService {
  private googleButtonContainer: HTMLElement | null = null;

  async initializeGoogleAuth(clientId: string) {
    console.log('🔐 Initializing Google OAuth with Client ID:', clientId);

    if (typeof window !== 'undefined' && window.google) {
      console.log('✅ Google Sign-In SDK loaded successfully');
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: this.handleGoogleResponse.bind(this),
        auto_select: false,
        cancel_on_tap_outside: true,
      });
      console.log('✅ Google Sign-In initialized with callback');
    } else {
      console.warn('⚠️ Google Sign-In SDK not loaded yet');
    }
  }

  async googleSignIn(): Promise<AuthResponse> {
    console.log('🚀 Starting Google Sign-In process...');
    return new Promise((resolve, reject) => {
      if (typeof window !== 'undefined' && window.google) {
        console.log('✅ Google SDK available, setting up promise handlers');
        this.currentPromise = { resolve, reject };

        // Skip the prompt and show button directly to avoid origin issues
        console.log('📱 Showing Google Sign-In button...');
        this.showGoogleSignInButton();
      } else {
        console.error('❌ Google Sign-In not initialized');
        reject(new Error('Google Sign-In not initialized'));
      }
    });
  }

  private showGoogleSignInButton() {
    // Remove any existing container first
    this.removeGoogleButtonContainer();

    // Create a temporary container for the Google sign-in button
    const container = document.createElement('div');
    container.style.position = 'fixed';
    container.style.top = '50%';
    container.style.left = '50%';
    container.style.transform = 'translate(-50%, -50%)';
    container.style.zIndex = '9999';
    container.style.backgroundColor = 'white';
    container.style.padding = '20px';
    container.style.borderRadius = '8px';
    container.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
    
    document.body.appendChild(container);
    this.googleButtonContainer = container;

    if (window.google) {
      window.google.accounts.id.renderButton(container, {
        theme: 'outline',
        size: 'large',
        width: '300'
      });
    }

    // Remove the container after 30 seconds as fallback
    setTimeout(() => {
      this.removeGoogleButtonContainer();
    }, 30000);
  }

  private removeGoogleButtonContainer() {
    if (this.googleButtonContainer && document.body.contains(this.googleButtonContainer)) {
      console.log('🧹 Removing Google Sign-In button container');
      document.body.removeChild(this.googleButtonContainer);
      this.googleButtonContainer = null;
    }
  }

  private currentPromise: { 
    resolve: (value: AuthResponse) => void; 
    reject: (reason?: any) => void 
  } | null = null;

  private async handleGoogleResponse(response: GoogleCredentialResponse) {
    console.log('🎉 Google OAuth callback received!');
    console.log('🔑 Credential token length:', response.credential.length);

    // Remove the Google button container immediately
    this.removeGoogleButtonContainer();

    try {
      console.log('🌐 Sending credential to beemi-server for admin verification...');
      const authResponse = await this.authenticateWithBeemiServer(response.credential);

      console.log('📥 Beemi-server response:', authResponse);

      if (!authResponse.success) {
        console.error('❌ Authentication failed:', authResponse.message);
        if (this.currentPromise) {
          this.currentPromise.resolve(authResponse);
          this.currentPromise = null;
        }
        return;
      }

      if (authResponse.success && authResponse.tokens) {
        console.log('✅ Authentication successful!');
        console.log('👤 Admin User:', authResponse.adminUser);
        console.log('🎫 Tokens received - storing locally');

        this.storeTokens(authResponse.tokens);

        const storedTokens = this.getStoredTokens();
        if (!storedTokens || !storedTokens.refreshToken) {
          console.error('❌ Failed to store tokens properly');
          throw new Error('Failed to store authentication tokens');
        }
        console.log('✅ Tokens verified in localStorage');
      }

      if (this.currentPromise) {
        console.log('🔄 Resolving authentication promise');
        this.currentPromise.resolve(authResponse);
        this.currentPromise = null;
      }
    } catch (error) {
      console.error('💥 Google authentication error:', error);
      if (this.currentPromise) {
        console.log('🔄 Rejecting authentication promise with error');
        this.currentPromise.reject(error);
        this.currentPromise = null;
      }
    }
  }

  private async authenticateWithBeemiServer(idToken: string): Promise<AuthResponse> {
    console.log('📦 Request payload: { idToken: [length=' + idToken.length + '] }');

    try {
      const response = await apiService.post<AuthResponse>('/api/admin/auth/google', {
        idToken
      });
      console.log('✅ API Response Success:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ API Error Response:', error.response?.data || error.message);
      const serverMessage = error.response?.data?.message;
      if (serverMessage) {
        throw new Error(serverMessage);
      } else {
        throw new Error(`Authentication failed: ${error.response?.status || 'Unknown'} - ${error.message}`);
      }
    }
  }

  private storeTokens(tokens: { access_token: string; refresh_token: string }) {
    console.log('💾 Storing authentication tokens in localStorage');
    localStorage.setItem('beemi_admin_access_token', tokens.access_token);
    localStorage.setItem('beemi_admin_refresh_token', tokens.refresh_token);
  }

  getStoredTokens() {
    const accessToken = localStorage.getItem('beemi_admin_access_token');
    const refreshToken = localStorage.getItem('beemi_admin_refresh_token');

    if (accessToken && refreshToken) {
      return { accessToken, refreshToken };
    }
    return null;
  }

  clearTokens() {
    console.log('🧹 Clearing authentication tokens from localStorage');
    localStorage.removeItem('beemi_admin_access_token');
    localStorage.removeItem('beemi_admin_refresh_token');
  }

  async verifyAdmin(): Promise<any> {
    console.log('👤 Verifying admin session...');
    const tokens = this.getStoredTokens();
    if (!tokens) {
      throw new Error('No authentication tokens found');
    }

    try {
      const response = await apiService.get('/api/admin/auth/verify');
      console.log('✅ Admin verified:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Failed to verify admin:', error.response?.data || error.message);
      throw new Error(`Failed to verify admin: ${error.response?.status || 'Unknown'}`);
    }
  }

  isAuthenticated(): boolean {
    return !!this.getStoredTokens();
  }

  async logout() {
    console.log('🚪 Logging out...');
    const tokens = this.getStoredTokens();
    
    try {
      if (tokens) {
        await apiService.post('/api/admin/auth/logout', {
          refresh_token: tokens.refreshToken
        });
      }
    } catch (error) {
      console.error('⚠️ Logout request failed:', error);
    } finally {
      this.clearTokens();
      if (typeof window !== 'undefined' && window.google) {
        window.google.accounts.id.disableAutoSelect();
      }
    }
  }
}

// Global Google type
declare global {
  interface Window {
    google: {
      accounts: {
        id: {
          initialize: (config: any) => void;
          prompt: (callback?: (notification: any) => void) => void;
          renderButton: (element: HTMLElement, config: any) => void;
          disableAutoSelect: () => void;
        };
      };
    };
  }
}

export default new AuthService();
